import { useState } from 'react';
import { Search, ChevronLeft, ChevronRight, Edit, Trash2, ChevronsUpDown } from 'lucide-react';
import CreateLabelModal from './CreateLabelModal';

interface Label {
  id: number;
  recordName: string;
  address: string;
  city: string;
  country: string;
  email: string;
  recordType: string;
  mktType: string;
  storeName: string;
}

export default function LabelScreen() {
  const [labels, setLabels] = useState<Label[]>([
    {
      id: 1,
      recordName: 'Mahuk Ltd.',
      address: '23 Shreres Dyche',
      city: 'United Kingdom',
      country: 'United Kingdom',
      email: 'uk.procurment@mahalkum.co',
      recordType: 'Originator',
      mktType: 'UK Market',
      storeName: 'Mahalkum'
    },
    {
      id: 2,
      recordName: 'Mahuk Ltd.',
      address: '23 Shreres Dyche',
      city: 'United Kingdom',
      country: 'United Kingdom',
      email: 'uk.procurment@mahalkum.co',
      recordType: 'Originator',
      mktType: 'UK Market',
      storeName: 'Mahalkum'
    },
    {
      id: 3,
      recordName: 'Mahuk Ltd.',
      address: '23 Shreres Dyche',
      city: 'United Kingdom',
      country: 'United Kingdom',
      email: 'uk.procurment@mahalkum.co',
      recordType: 'Originator',
      mktType: 'UK Market',
      storeName: 'Mahalkum'
    },
    {
      id: 4,
      recordName: 'Mahuk Ltd.',
      address: '23 Shreres Dyche',
      city: 'United Kingdom',
      country: 'United Kingdom',
      email: 'uk.procurment@mahalkum.co',
      recordType: 'Originator',
      mktType: 'UK Market',
      storeName: 'Mahalkum'
    },
    {
      id: 5,
      recordName: 'Mahuk Ltd.',
      address: '23 Shreres Dyche',
      city: 'United Kingdom',
      country: 'United Kingdom',
      email: 'uk.procurment@mahalkum.co',
      recordType: 'Originator',
      mktType: 'UK Market',
      storeName: 'Mahalkum'
    }
  ]);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRecordType] = useState('all');
  const [selectedMktType] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [selectedLabels, setSelectedLabels] = useState<number[]>([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Filter labels based on search and filters
  const filteredLabels = labels.filter(label => {
    const matchesSearch = label.recordName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         label.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         label.address.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRecordType = selectedRecordType === 'all' || label.recordType === selectedRecordType;
    const matchesMktType = selectedMktType === 'all' || label.mktType === selectedMktType;
    
    return matchesSearch && matchesRecordType && matchesMktType;
  });

  // Pagination
  const totalItems = filteredLabels.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);
  const paginatedLabels = filteredLabels.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedLabels(paginatedLabels.map(label => label.id));
    } else {
      setSelectedLabels([]);
    }
  };

  const handleSelectLabel = (labelId: number, checked: boolean) => {
    if (checked) {
      setSelectedLabels(prev => [...prev, labelId]);
    } else {
      setSelectedLabels(prev => prev.filter(id => id !== labelId));
    }
  };

  const handleDeleteLabel = (labelId: number) => {
    setLabels(prev => prev.filter(label => label.id !== labelId));
  };

  const handleEditLabel = (labelId: number) => {
    // Handle edit label logic
    console.log('Edit label:', labelId);
  };

  const handleCreateLabel = () => {
    setIsCreateModalOpen(true);
  };

  const handleCreateLabelSubmit = async (labelData: any) => {
    // Handle create label logic
    console.log('Creating new label:', labelData);
    // Here you would typically make an API call to create the label
    // For now, we'll just add it to the local state
    const newLabel: Label = {
      id: Math.max(...labels.map(l => l.id)) + 1,
      recordName: labelData.name,
      address: labelData.address || '',
      city: labelData.city || '',
      country: labelData.countryOrigin || '',
      email: labelData.email || '',
      recordType: labelData.recordType || 'Type 1',
      mktType: labelData.mktType || 'Type 1',
      storeName: labelData.companyName || ''
    };
    setLabels(prev => [...prev, newLabel]);
  };

  // Custom checkbox component
  const CustomCheckbox = ({ 
    checked, 
    onChange, 
    className = "" 
  }: { 
    checked: boolean; 
    onChange: (checked: boolean) => void;
    className?: string;
  }) => {
    return (
      <div className={`relative ${className}`}>
        <input 
          type="checkbox" 
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="peer w-4 h-4 rounded border-2 border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-2 bg-white appearance-none checked:bg-blue-600 checked:border-blue-600" 
        />
        <svg 
          className="absolute inset-0 w-4 h-4 pointer-events-none opacity-0 peer-checked:opacity-100 text-white" 
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
    );
  };

  return (
    <div className="mx-8 space-y-4">
      <div className="flex items-center justify-between rounded-[20px] border border-[#ECEFF6] bg-white px-4 py-2.5">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8C97B5] w-4 h-4" />
          <input
            type="text"
            placeholder="Search for Orders"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="h-9 w-[250px] rounded-full border border-[#DCE2F0] bg-[#FBFCFF] pl-10 pr-4 text-[13px] font-medium text-[#36415E] placeholder:text-[#9AA5C4] focus:outline-none focus:ring-2 focus:ring-yellow-400"
          />
        </div>
        <button 
          onClick={handleCreateLabel}
          className="h-10 w-[116px] rounded-lg border border-[#F6C600] text-[14px] font-medium text-[#F1BE00] transition-colors hover:bg-[#FFF9E1]"
        >
          Create
        </button>
      </div>

      <div className="overflow-hidden rounded-[8px] border border-[#E6EBF5] bg-white">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-[#EEF2FF] border-b border-[#E4E9F4]">
              <th className="py-3 px-4 text-left border-r border-[#E4E9F4]">
                <CustomCheckbox 
                  checked={selectedLabels.length === paginatedLabels.length && paginatedLabels.length > 0}
                  onChange={handleSelectAll}
                />
              </th>
              <th className="py-3 px-4 text-left text-[12px] font-medium text-[#5D6786] border-r border-[#E4E9F4]">
                <div className="flex items-center gap-1">
                  Actions
                  <ChevronsUpDown size={12} className="text-[#9BA8C9]" />
                </div>
              </th>
              <th className="py-3 px-4 text-left text-[12px] font-medium text-[#5D6786] border-r border-[#E4E9F4]">
                <div className="flex items-center gap-1">
                  Record Name
                  <ChevronsUpDown size={12} className="text-[#9BA8C9]" />
                </div>
              </th>
              <th className="py-3 px-4 text-left text-[12px] font-medium text-[#5D6786] border-r border-[#E4E9F4]">
                <div className="flex items-center gap-1">
                  Address
                  <ChevronsUpDown size={12} className="text-[#9BA8C9]" />
                </div>
              </th>
              <th className="py-3 px-4 text-left text-[12px] font-medium text-[#5D6786] border-r border-[#E4E9F4]">
                <div className="flex items-center gap-1">
                  City
                  <ChevronsUpDown size={12} className="text-[#9BA8C9]" />
                </div>
              </th>
              <th className="py-3 px-4 text-left text-[12px] font-medium text-[#5D6786] border-r border-[#E4E9F4]">
                <div className="flex items-center gap-1">
                  Country
                  <ChevronsUpDown size={12} className="text-[#9BA8C9]" />
                </div>
              </th>
              <th className="py-3 px-4 text-left text-[12px] font-medium text-[#5D6786] border-r border-[#E4E9F4]">
                <div className="flex items-center gap-1">
                  Email
                  <ChevronsUpDown size={12} className="text-[#9BA8C9]" />
                </div>
              </th>
              <th className="py-3 px-4 text-left text-[12px] font-medium text-[#5D6786] border-r border-[#E4E9F4]">
                <div className="flex items-center gap-1">
                  Record Type
                  <ChevronsUpDown size={12} className="text-[#9BA8C9]" />
                </div>
              </th>
              <th className="py-3 px-4 text-left text-[12px] font-medium text-[#5D6786] border-r border-[#E4E9F4]">
                <div className="flex items-center gap-1">
                  MKT Type
                  <ChevronsUpDown size={12} className="text-[#9BA8C9]" />
                </div>
              </th>
              <th className="py-3 px-4 text-left text-[12px] font-medium text-[#5D6786]">
                <div className="flex items-center gap-1">
                  Store Name
                  <ChevronsUpDown size={12} className="text-[#9BA8C9]" />
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#EEF2FA]">
            {paginatedLabels.map((label) => (
              <tr key={label.id} className="hover:bg-[#FAFCFF]">
                <td className="py-3 px-4 border-r border-[#E9EDF7]">
                  <CustomCheckbox 
                    checked={selectedLabels.includes(label.id)}
                    onChange={(checked) => handleSelectLabel(label.id, checked)}
                  />
                </td>
                <td className="py-3 px-4 border-r border-[#E9EDF7]">
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => handleEditLabel(label.id)}
                      className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#EEF2FF] text-[#7C8DEE] text-xs font-medium hover:bg-[#E2E8FF] transition-colors"
                    >
                      <Edit size={14} />
                      Edit
                    </button>
                    <button 
                      onClick={() => handleDeleteLabel(label.id)}
                      className="inline-flex items-center justify-center w-6 h-6 rounded text-[#FF4A77] hover:bg-[#FFEFF4] transition-colors"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </td>
                <td className="py-3 px-4 text-xs text-[#344160] border-r border-[#E9EDF7]">
                  {label.recordName}
                </td>
                <td className="py-3 px-4 text-xs text-[#344160] border-r border-[#E9EDF7]">
                  {label.address}
                </td>
                <td className="py-3 px-4 text-xs text-[#344160] border-r border-[#E9EDF7]">
                  {label.city}
                </td>
                <td className="py-3 px-4 text-xs text-[#344160] border-r border-[#E9EDF7]">
                  {label.country}
                </td>
                <td className="py-3 px-4 text-xs text-[#344160] border-r border-[#E9EDF7]">
                  {label.email}
                </td>
                <td className="py-3 px-4 text-xs text-[#344160] border-r border-[#E9EDF7]">
                  {label.recordType}
                </td>
                <td className="py-3 px-4 text-xs text-[#344160] border-r border-[#E9EDF7]">
                  {label.mktType}
                </td>
                <td className="py-3 px-4 text-xs text-[#344160]">
                  {label.storeName}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      

      <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-[#EEF2FA] text-xs text-[#5C6885]">
        <div className="flex items-center gap-2">
          <span>Show</span>
          <select
            value={itemsPerPage}
            onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
            className="h-7 rounded-md border border-[#DFE5F3] bg-[#F8FAFF] px-2 text-xs focus:outline-none focus:ring-1 focus:ring-yellow-400"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
          </select>
          <span>per page</span>
          <span className="ml-6">
            {startItem}-{endItem} of {totalItems}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="rounded px-1.5 py-0.5 hover:bg-[#F3F6FF] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4 text-[#5C6885]" />
          </button>
          {[1, 2, 3, 4, 5].map((pageNum) => (
            <button
              key={pageNum}
              onClick={() => handlePageChange(pageNum)}
              className={`h-6 w-6 rounded text-xs ${
                currentPage === pageNum
                  ? 'bg-[#EEF2FF] text-[#415178]'
                  : 'text-[#5C6885] hover:bg-[#F3F6FF]'
              }`}
            >
              {pageNum}
            </button>
          ))}
          <button
            onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="rounded px-1.5 py-0.5 hover:bg-[#F3F6FF] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-4 h-4 text-[#5C6885]" />
          </button>
        </div>
      </div>
      </div>

      <CreateLabelModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateLabelSubmit}
      />
    </div>
  );
}
